import type { ConnectorAssetResponsePayload } from "../../../../services/api";
import type { SchemaTreeNode, SchemaTreeTable } from "../../../molecules";

export interface TableMeta {
  schema: string;
  table: string;
  rowCount: number;
  columnCount: number;
  tableAssetId: string;
  /** Salesforce/HubSpot-style system object (Feed, History, Share, …) —
   * queryable but empty unless specific org activity happened. Hidden by
   * default in the Select step behind a "Show system objects" toggle. */
  isSystem: boolean;
  /** Raw API name (``asset.name``) — distinct from ``table``, which may be
   * the display label (``asset.display_name``). Needed anywhere the name is
   * sent back to the connector's API (e.g. Salesforce SOQL/describe calls),
   * since a display label like "My Custom Object" isn't a valid API name. */
  objectName: string;
  /** Whether ``rowCount`` reflects a real fetched value vs. the "unknown"
   * fallback (0, same as a genuinely-empty object — parseNumber can't tell
   * them apart). Needed to filter out empty objects without also hiding ones
   * we simply haven't counted yet (e.g. system objects skipped from the
   * front-load, or a connection that predates row-count support). */
  rowCountKnown: boolean;
  /** Raw ``connector_assets.asset_type`` (e.g. "saas_object", "bi_view") —
   * used to distinguish row kinds within one connector's tree, e.g.
   * Salesforce's Objects vs. natively-synced Reports & Dashboards, which
   * share the same flat tree but only Objects get a preview affordance.
   * Optional since not every mapper populates it (BI tree leaves don't need
   * it — they're already a single, uniform kind). */
  assetType?: string;
}

export interface SelectTreeMapResult {
  schemaNodes: SchemaTreeNode[];
  tableMetaByKey: Record<string, TableMeta>;
}

export interface SelectTreeMapResultWithPreselect extends SelectTreeMapResult {
  preSelectedKeys: Set<string>;
}

export interface BiDatasetTreeNode {
  id: string;
  key: string;
  label: string;
  assetId?: string;
  children: BiDatasetTreeNode[];
}

export interface BiDatasetTreeMapResult {
  nodes: BiDatasetTreeNode[];
  tableMetaByKey: Record<string, TableMeta>;
}

export const qualifiedKey = (schema: string, table: string) =>
  `${schema}.${table}`;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const parseNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const resolveLabel = (asset: ConnectorAssetResponsePayload): string =>
  (asset.display_name ?? asset.name).trim() || asset.name || asset.id;

const sortByLabel = <T extends { name?: string; label?: string }>(
  items: T[],
  getLabel: (item: T) => string,
): T[] => [...items].sort((a, b) => getLabel(a).localeCompare(getLabel(b)));

const buildSchemaNodes = (
  groupedTables: Map<string, SchemaTreeTable[]>,
): SchemaTreeNode[] =>
  Array.from(groupedTables.entries())
    .map(([name, tables]) => ({
      name,
      tables: tables.sort((a, b) => a.table.localeCompare(b.table)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

// ---------------------------------------------------------------------------
// Flat-group mapper (max_depth=1)
//
// Used for connectors like HubSpot where all selectable items are at depth=1
// with no children.  Groups them under a single virtual schema node so the
// SchemaTree's two-level layout still works.
// ---------------------------------------------------------------------------

export const mapFlatAssetsAsGroup = (
  items: ConnectorAssetResponsePayload[],
  groupName: string,
): SelectTreeMapResultWithPreselect => {
  const tableMetaByKey: Record<string, TableMeta> = {};
  const preSelectedKeys = new Set<string>();
  const tables: SchemaTreeTable[] = [];

  for (const item of items) {
    const tableName = resolveLabel(item);
    const key = qualifiedKey(groupName, tableName);

    tableMetaByKey[key] = {
      schema: groupName,
      table: tableName,
      rowCount:
        typeof item.row_count === "number"
          ? item.row_count
          : parseNumber(item.attributes_json.row_count),
      columnCount:
        typeof item.column_count === "number"
          ? item.column_count
          : parseNumber(item.attributes_json.column_count),
      tableAssetId: item.id,
      isSystem: item.attributes_json.is_system === true,
      objectName: item.name,
      rowCountKnown:
        typeof item.row_count === "number" ||
        typeof item.attributes_json.row_count === "number",
      assetType: item.asset_type,
    };

    tables.push({ schema: groupName, table: tableName });

    if (item.is_selected) {
      preSelectedKeys.add(key);
    }
  }

  return {
    schemaNodes: [
      {
        name: groupName,
        tables: tables.sort((a, b) => a.table.localeCompare(b.table)),
      },
    ],
    tableMetaByKey,
    preSelectedKeys,
  };
};

// ---------------------------------------------------------------------------
// Tree-response mapper (max_depth >= 2, view=tree)
//
// The backend returns items already nested: depth-1 items are schemas,
// each carrying a `children` array of depth-2 tables.  We traverse that
// structure directly — no parent-id resolution needed.
// ---------------------------------------------------------------------------

export const mapAssetsFromTreeResponse = (
  treeItems: ConnectorAssetResponsePayload[],
): SelectTreeMapResultWithPreselect => {
  const tableMetaByKey: Record<string, TableMeta> = {};
  const groupedTables = new Map<string, SchemaTreeTable[]>();
  const preSelectedKeys = new Set<string>();

  for (const schemaItem of treeItems) {
    const schemaName = resolveLabel(schemaItem);

    if (!groupedTables.has(schemaName)) {
      groupedTables.set(schemaName, []);
    }

    for (const tableItem of schemaItem.children ?? []) {
      const tableName = resolveLabel(tableItem);
      const key = qualifiedKey(schemaName, tableName);

      tableMetaByKey[key] = {
        schema: schemaName,
        table: tableName,
        rowCount:
          typeof tableItem.row_count === "number"
            ? tableItem.row_count
            : parseNumber(tableItem.attributes_json.row_count),
        columnCount:
          typeof tableItem.column_count === "number"
            ? tableItem.column_count
            : parseNumber(tableItem.attributes_json.column_count),
        tableAssetId: tableItem.id,
        isSystem: tableItem.attributes_json.is_system === true,
        objectName: tableItem.name,
        rowCountKnown:
          typeof tableItem.row_count === "number" ||
          typeof tableItem.attributes_json.row_count === "number",
        assetType: tableItem.asset_type,
      };

      const existing = groupedTables.get(schemaName) ?? [];
      groupedTables.set(schemaName, [
        ...existing,
        { schema: schemaName, table: tableName },
      ]);

      if (tableItem.is_selected) {
        preSelectedKeys.add(key);
      }
    }
  }

  return {
    schemaNodes: buildSchemaNodes(groupedTables),
    tableMetaByKey,
    preSelectedKeys,
  };
};

// ---------------------------------------------------------------------------
// BI tree mappers
// ---------------------------------------------------------------------------

const buildBiLeafKey = (path: string[]): string => {
  if (path.length >= 2)
    return qualifiedKey(
      path[path.length - 2] ?? "Catalog",
      path[path.length - 1] ?? "Item",
    );
  if (path.length === 1) return qualifiedKey("Catalog", path[0] ?? "Item");
  return qualifiedKey("Catalog", "Item");
};

const buildBiLeafMeta = (
  asset: ConnectorAssetResponsePayload,
  nodePath: string[],
  nodeLabel: string,
): TableMeta => ({
  schema:
    nodePath.length > 1 ? (nodePath[nodePath.length - 2] ?? "Catalog") : "Catalog",
  table: nodePath[nodePath.length - 1] ?? nodeLabel,
  rowCount:
    typeof asset.row_count === "number"
      ? asset.row_count
      : parseNumber(asset.attributes_json.row_count),
  columnCount:
    typeof asset.column_count === "number"
      ? asset.column_count
      : parseNumber(asset.attributes_json.column_count),
  tableAssetId: asset.id,
  isSystem: asset.attributes_json.is_system === true,
  objectName: asset.name,
  rowCountKnown:
    typeof asset.row_count === "number" ||
    typeof asset.attributes_json.row_count === "number",
});

/**
 * Maps the tree-structured API response (view=tree) to BiDatasetTreeNode[].
 * Each item already carries its children — no parent-id resolution needed.
 */
export const mapBiAssetsFromTreeResponse = (
  treeItems: ConnectorAssetResponsePayload[],
): BiDatasetTreeMapResult => {
  const tableMetaByKey: Record<string, TableMeta> = {};

  const visit = (
    asset: ConnectorAssetResponsePayload,
    parentPath: string[],
  ): BiDatasetTreeNode => {
    const nodeLabel = resolveLabel(asset);
    const nodePath = [...parentPath, nodeLabel];
    const children = sortByLabel(asset.children ?? [], resolveLabel).map(
      (child) => visit(child, nodePath),
    );
    const key = buildBiLeafKey(nodePath);
    const isLeaf = children.length === 0;

    if (isLeaf) {
      tableMetaByKey[key] = buildBiLeafMeta(asset, nodePath, nodeLabel);
    }

    return {
      id: asset.id,
      key,
      label: nodeLabel,
      assetId: isLeaf ? asset.id : undefined,
      children,
    };
  };

  return {
    nodes: sortByLabel(treeItems, resolveLabel).map((item) => visit(item, [])),
    tableMetaByKey,
  };
};

/**
 * Flat-list variant — kept for connectors whose backend doesn't yet support
 * view=tree (resolves parent-child links via parent_asset_id).
 */
export const mapAssetsForBiDatasetTree = (
  allAssets: ConnectorAssetResponsePayload[],
): BiDatasetTreeMapResult => {
  const byId = new Map(allAssets.map((asset) => [asset.id, asset] as const));
  const childrenByParent = new Map<string, ConnectorAssetResponsePayload[]>();
  const tableMetaByKey: Record<string, TableMeta> = {};

  for (const asset of allAssets) {
    if (!asset.parent_asset_id) continue;
    const siblings = childrenByParent.get(asset.parent_asset_id) ?? [];
    siblings.push(asset);
    childrenByParent.set(asset.parent_asset_id, siblings);
  }

  const rootAssets = sortByLabel(
    allAssets.filter(
      (asset) => !asset.parent_asset_id || !byId.has(asset.parent_asset_id),
    ),
    resolveLabel,
  );

  const visit = (
    asset: ConnectorAssetResponsePayload,
    parentPath: string[],
  ): BiDatasetTreeNode => {
    const nodeLabel = resolveLabel(asset);
    const nodePath = [...parentPath, nodeLabel];
    const children = sortByLabel(
      childrenByParent.get(asset.id) ?? [],
      resolveLabel,
    ).map((child) => visit(child, nodePath));
    const key = buildBiLeafKey(nodePath);
    const isLeaf = children.length === 0;

    if (isLeaf) {
      tableMetaByKey[key] = buildBiLeafMeta(asset, nodePath, nodeLabel);
    }

    return {
      id: asset.id,
      key,
      label: nodeLabel,
      assetId: isLeaf ? asset.id : undefined,
      children,
    };
  };

  return {
    nodes: rootAssets.map((asset) => visit(asset, [])),
    tableMetaByKey,
  };
};
